import { Message } from 'element-ui'
import { fetchAppModules } from '@/api/account'

/**
 * 左侧菜单 / 页面 key → t_roles_tree 节点名（叶子 + 父级）
 * 父级名仅在勾选父节点本身时出现；勾选叶子只会带叶子名，实现细化控制
 */
const MENU_MODULES = {
  // 数据服务
  browser: ['数据服务', '查看数据', '管理数据'],
  // IOT
  mqtt: ['IOT管理', '查看设备', '管理设备', '设备在线状态'],
  // 云函数
  cloud: ['云函数', '查看云函数', '管理云函数', '查看云函数分类', '管理云函数分类', '定时任务', '定时任务日志', '钩子配置', '查看Node云函数', '管理Node云函数', '查看Node云函数分类', '管理Node云函数分类'],
  // 素材：页面级拆分（上传与删除/管理分离）
  fileManager: ['素材管理', '查看文件', '上传文件', '管理文件'],
  filemaintain: ['素材管理', '查看应用文件', '上传应用文件', '管理应用文件'],
  staticSite: ['素材管理', '静态网站部署'],
  material: ['素材管理', '查看文件', '上传文件', '管理文件'],
  // 收益
  appPay: ['收益管理', '支付统计', '小程序订单', '虚拟支付', '回调日志', '收益回调配置'],
  // 短信
  sms: ['短信服务', '查看短信', '管理短信'],
  // 推送
  pushmsg: ['消息推送', '查看推送', '发送与设置'],
  // 统计分析（与侧栏一一对应）
  analysis: ['统计分析', 'API统计', 'AI统计', '存储统计', '流量统计', '邮件统计', '实时API'],
  analysisApiAll: ['统计分析', 'API统计'],
  analysisToken: ['统计分析', 'AI统计'],
  analysisStorage: ['统计分析', '存储统计'],
  analysisFlow: ['统计分析', '流量统计'],
  analysisEmail: ['统计分析', '邮件统计'],
  analysisApi: ['统计分析', '实时API'],
  // 应用设置（与侧栏一一对应）
  secret: ['应用设置', '应用密钥'],
  domain: ['应用设置', '域名管理'],
  appauth: ['应用设置', '安全验证'],
  backuprestore: ['应用设置', '备份还原'],
  config: ['应用设置', '应用配置'],
  setemail: ['应用设置', '邮件设置'],
  edit: ['应用设置', '基础设置'],
  aiset: ['AI设置', '查看AI配置', '管理AI配置'],
  dataHook: ['云函数', '钩子配置'],
  upgrade: ['套餐升级', '套餐与资源']
}

// 顶层「素材」入口：任一子能力即可进入
const MATERIAL_ANY = ['素材管理', '查看文件', '上传文件', '管理文件', '静态网站部署', '查看应用文件', '上传应用文件', '管理应用文件']

let cache = {
  appId: '',
  isOwner: true,
  modules: ['*'],
  loading: null
}

function pick(row, ...keys) {
  for (let i = 0; i < keys.length; i++) {
    if (row[keys[i]] !== undefined && row[keys[i]] !== null) {
      return row[keys[i]]
    }
  }
  return ''
}

export function loadAppPermission(appId) {
  const id = String(appId || '')
  if (!id) {
    cache = { appId: '', isOwner: true, modules: ['*'], loading: null }
    return Promise.resolve(cache)
  }
  if (cache.appId === id && cache.loading === null) {
    return Promise.resolve(cache)
  }
  if (cache.appId === id && cache.loading) {
    return cache.loading
  }
  cache.appId = id
  cache.loading = fetchAppModules(id).then(res => {
    const data = (res && res.data) || {}
    const isOwner = !!pick(data, 'isOwner', 'IsOwner')
    let modules = pick(data, 'modules', 'Modules') || []
    if (!Array.isArray(modules)) {
      modules = []
    }
    cache = {
      appId: id,
      isOwner,
      modules,
      loading: null
    }
    return cache
  }).catch(() => {
    cache = {
      appId: id,
      isOwner: false,
      modules: [],
      loading: null
    }
    return cache
  })
  return cache.loading
}

export function clearAppPermission() {
  cache = { appId: '', isOwner: true, modules: ['*'], loading: null }
}

export function getAppPermissionCache() {
  return cache
}

export function canAccessModules(moduleNames) {
  if (cache.isOwner) return true
  if (cache.modules.indexOf('*') !== -1) return true
  const names = Array.isArray(moduleNames) ? moduleNames : [moduleNames]
  return names.some(name => cache.modules.indexOf(name) !== -1)
}

export function canAccessMenu(menuKey) {
  if (menuKey === 'materialNav') {
    return canAccessModules(MATERIAL_ANY)
  }
  const modules = MENU_MODULES[menuKey]
  if (!modules || !modules.length) {
    return cache.isOwner
  }
  return canAccessModules(modules)
}

/** 文件管理：建目录/重命名/删除 —— 仅认「管理文件」 */
export function canManageFiles() {
  return canAccessModules(['管理文件'])
}

/** 文件管理：上传 ——「上传文件」或「管理文件」均可 */
export function canUploadFiles() {
  return canAccessModules(['上传文件', '管理文件'])
}

/** 文件管理：浏览列表 */
export function canViewFiles() {
  return canAccessModules(['查看文件', '上传文件', '管理文件'])
}

/** 应用文件日志：删除/完整管理 —— 仅认「管理应用文件」 */
export function canManageAppFiles() {
  return canAccessModules(['管理应用文件'])
}

/** 应用文件日志：上传 ——「上传应用文件」或「管理应用文件」均可 */
export function canUploadAppFiles() {
  return canAccessModules(['上传应用文件', '管理应用文件'])
}

/** 应用文件日志：浏览 */
export function canViewAppFiles() {
  return canAccessModules(['查看应用文件', '上传应用文件', '管理应用文件'])
}

export function guardMenu(menuKey, onAllow) {
  if (canAccessMenu(menuKey)) {
    if (typeof onAllow === 'function') onAllow()
    return true
  }
  Message.warning('您没有该功能的访问权限，请联系应用管理员分配权限')
  return false
}

/** 素材顶栏：跳转到当前用户有权限的第一个子页 */
export function resolveMaterialEntryPath(appId) {
  const id = String(appId || '')
  if (canAccessMenu('fileManager')) return '/app/fileManager/' + id
  if (canAccessMenu('staticSite')) return '/app/staticSite/' + id
  if (canAccessMenu('filemaintain')) return '/app/filemaintain/' + id
  return '/app/fileManager/' + id
}

/** 分析菜单：跳到当前用户有权限的第一个统计子页 */
export function resolveAnalysisEntryPath(appId) {
  const id = String(appId || '')
  const entries = [
    ['analysisApiAll', '/analysis/ApiAll/'],
    ['analysisToken', '/analysis/Token/'],
    ['analysisStorage', '/analysis/Storage/'],
    ['analysisFlow', '/analysis/Flow/'],
    ['analysisEmail', '/analysis/Email/'],
    ['analysisApi', '/analysis/Api/']
  ]
  for (let i = 0; i < entries.length; i++) {
    if (canAccessMenu(entries[i][0])) {
      return entries[i][1] + id
    }
  }
  return '/analysis/ApiAll/' + id
}

/**
 * 进入应用时的落地页：按侧栏顺序找第一个有权限的页面
 * 避免协作成员只有「统计分析」等权限时仍被踢到 /app/browser
 */
export function resolveAppEntryPath(appId) {
  const id = String(appId || '')
  if (canAccessMenu('browser')) return '/app/browser/' + id
  if (canAccessMenu('mqtt')) return '/mqtt/index/' + id
  if (canAccessMenu('cloud')) return '/app/cloud/' + id
  if (canAccessMenu('materialNav')) return resolveMaterialEntryPath(id)
  if (canAccessMenu('appPay')) return '/appPay/wechatlog/' + id
  if (canAccessMenu('sms')) return '/sms/template/' + id
  if (canAccessMenu('pushmsg')) return '/app/pushmsg/' + id
  if (canAccessMenu('analysis')) return resolveAnalysisEntryPath(id)
  if (canAccessMenu('secret')) return '/app/secret/' + id
  if (canAccessMenu('domain')) return '/app/domain/' + id
  if (canAccessMenu('appauth')) return '/app/appauth/' + id
  if (canAccessMenu('backuprestore')) return '/app/backuprestore/' + id
  if (canAccessMenu('config')) return '/app/config/' + id
  if (canAccessMenu('setemail')) return '/app/setemail/' + id
  if (canAccessMenu('edit')) return '/app/edit/' + id
  if (canAccessMenu('upgrade')) return '/upgrade/applevel/' + id
  if (canAccessMenu('aiset')) return '/app/aiset/' + id
  return '/app/browser/' + id
}

export function resolveMenuKeyByPath(path) {
  const p = String(path || '')
  if (p.includes('/browser')) return 'browser'
  if (p.includes('/mqtt')) return 'mqtt'
  if (p.includes('/cloud') || p.includes('/Cloud') || p.includes('/addCloud') || p.includes('/cloudCategory') || p.includes('/dataHook')) {
    if (p.includes('/dataHook')) return 'dataHook'
    return 'cloud'
  }
  if (p.includes('/filemaintain')) return 'filemaintain'
  if (p.includes('/staticSite')) return 'staticSite'
  if (p.includes('/fileManager') || p.includes('/material')) return 'fileManager'
  if (p.includes('/appPay')) return 'appPay'
  if (p.includes('/sms')) return 'sms'
  if (p.includes('/pushmsg')) return 'pushmsg'
  if (p.includes('/analysis/ApiAll') || p.includes('/analysis/apiAll')) return 'analysisApiAll'
  if (p.includes('/analysis/Token') || p.includes('/analysis/token')) return 'analysisToken'
  if (p.includes('/analysis/Storage') || p.includes('/analysis/storage')) return 'analysisStorage'
  if (p.includes('/analysis/Flow') || p.includes('/analysis/flow')) return 'analysisFlow'
  if (p.includes('/analysis/Email') || p.includes('/analysis/email')) return 'analysisEmail'
  if (p.includes('/analysis/Api') || p.includes('/analysis/api')) return 'analysisApi'
  if (p.includes('/analysis')) return 'analysis'
  if (p.includes('/upgrade')) return 'upgrade'
  if (p.includes('/aiset')) return 'aiset'
  if (p.includes('/secret')) return 'secret'
  if (p.includes('/domain')) return 'domain'
  if (p.includes('/appauth')) return 'appauth'
  if (p.includes('/backuprestore')) return 'backuprestore'
  if (p.includes('/config')) return 'config'
  if (p.includes('/setemail')) return 'setemail'
  if (p.includes('/edit')) return 'edit'
  return ''
}
